import React, { createContext } from 'react';
import { AxiosResponse } from 'axios';

import { Appointment, Device, Loadable } from '../types';
import { appointmentService } from '../api/services/appointmentService';
import { convertUTCToLocal } from '../utils/timezoneUtils';
import { useDeviceCollection } from '../hooks';

interface AppointmentContextType {
  device?: Device;
  appointments: Loadable<Appointment>;
  saveAppointment: (appointment: Appointment) => Promise<void>;
  removeAppointment: (appointmentId: string) => Promise<void>;
}

const loadableAppointments: Loadable<Appointment> = { loading: true };

const initialContext: AppointmentContextType = {
  appointments: loadableAppointments,
  saveAppointment: () => Promise.resolve(),
  removeAppointment: () => Promise.resolve(),
};

const AppointmentContext = createContext<AppointmentContextType>(initialContext);

interface AppointmentProviderProps {
  device?: Device;
  children: React.ReactNode;
}

const mapAppointments = (data: Appointment[]): Appointment[] =>
  data
    .filter((a) => a.date && a.time)
    .map((a) => {
      let localTime = a.time;
      try {
        if (a.time) {
          localTime = convertUTCToLocal(a.time);
        }
      } catch (error) {
        console.warn('Failed to convert appointment time from UTC to local:', error);
      }

      return {
        ...a,
        time: localTime,
        createdAt: new Date(a.createdAt),
      };
    });

function AppointmentProvider({ device, children }: AppointmentProviderProps) {
  const { items: appointments, setItems: setAppointments } = useDeviceCollection<Appointment>(
    device,
    appointmentService.getAppointments,
    { mapper: mapAppointments },
  );

  const saveAppointment = (appointment: Appointment) => {
    if (!device) return Promise.reject('no device');

    return new Promise<void>((resolve, reject) => {
      const handleResponse = (response: AxiosResponse<Appointment>, isNew: boolean) => {
        let update = { ...response.data };

        try {
          if (update.time) {
            update.time = convertUTCToLocal(update.time);
          }
        } catch (error) {
          console.warn(
            'Failed to convert appointment time from UTC to local in save response:',
            error,
          );
        }

        const existing = appointments.data ?? [];
        const data = isNew
          ? [...existing, update]
          : existing.map((c) => (c.id === appointment.id ? update : c));
        setAppointments({ loading: false, data });
        resolve();
      };

      if (appointment.id) {
        appointmentService
          .updateAppointment(device.id, appointment.id, appointment)
          .then((response) => handleResponse(response, false))
          .catch((err) => {
            setAppointments({ loading: false, error: err });
            reject(err);
          });
      } else {
        appointmentService
          .createAppointment(device.id, appointment)
          .then((response) => handleResponse(response, true))
          .catch((err) => {
            setAppointments({ loading: false, error: err });
            reject(err);
          });
      }
    });
  };

  const removeAppointment = async (appointmentId: string) => {
    if (!device) return Promise.reject('no device');
    try {
      await appointmentService.deleteAppointment(device.id, appointmentId);
      return setAppointments({
        loading: false,
        data: appointments.data?.filter((c) => c.id !== appointmentId),
      });
    } catch (err) {
      return setAppointments({ loading: false, error: err as Error });
    }
  };

  return (
    <AppointmentContext.Provider
      value={{
        device,
        appointments,
        saveAppointment,
        removeAppointment,
      }}
    >
      {children}
    </AppointmentContext.Provider>
  );
}

export { AppointmentContext, AppointmentProvider };
